import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ImageUploader from '../../components/admin/ImageUploader';

export default function FloorPlansManager() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const fullPlans = [
    {
      id: '2-1',
      title: '2+1 Örnek Daire',
      net: '85',
      gross: '110',
      desc: '2+1 daire tipimiz, modern yaşam alanları arayanlar için ideal bir seçenektir. Geniş salon alanı, fonksiyonel mutfak ve konforlu yatak odaları ile ailenizin tüm ihtiyaçlarını karşılayacak şekilde tasarlanmıştır.',
      image: '/tk-int-4.png',
      blueprint: '/plan-2-1.png'
    },
    {
      id: '2-1-garden',
      title: '2+1 Bahçe Katı',
      net: '95',
      gross: '125',
      desc: 'Doğayla iç içe bir yaşam sunan bahçe katı dairelerimiz, size özel yeşil alanlarıyla müstakil ev konforu yaşatır. Sabah kahvelerinizi kendi bahçenizde içmenin keyfine varın.',
      image: '/tk-int-ref-1.png',
      blueprint: '/plan-2-1-garden.png'
    },
    {
      id: '3-1',
      title: '3+1 Klasik Aile',
      net: '120',
      gross: '155',
      desc: 'Geniş aileler için tasarlanmış 3+1 daire tipimiz, konforlu ve fonksiyonel bir yaşam sunar. Ebeveyn giyinme odası, geniş balkonlar ve ferah odalar klasik aile yaşantısına lüks katıyor.',
      image: '/tk-int-1.png',
      blueprint: '/plan-3-1.png'
    },
    {
      id: '3-1-terrace',
      title: '3+1 Panoramik Teras',
      net: '135',
      gross: '175',
      desc: 'Gökyüzü konforunu evinize taşıyan bu özel plan, devasa panoromik terası ile şehri ve peyzajı ayaklarınızın altına seriyor. Lüks bir loft hissi arayanlar için tasarlandı.',
      image: '/yapsat-realistic-3.png',
      blueprint: '/plan-3-1-terrace.png'
    },
    {
      id: '3-1-duplex',
      title: '3+1 Dubleks',
      net: '145',
      gross: '185',
      desc: 'İki katlı özel yaşam alanı arayanlar için kusursuz bir mimari bütünlük. Alt katta geniş sosyal alanlar yer alırken, üst katta tamamen izole edilmiş mahrem ve rahat yatak odaları bulunur.',
      image: '/yapsat-int-3.png',
      blueprint: '/plan-3-1-duplex.png'
    },
    {
      id: '4-1',
      title: '4+1 Ekstra Geniş',
      net: '180',
      gross: '220',
      desc: 'KARE YAPI\'nın en prestijli konut tiplerinden biri. Hem kalabalık aileler, hem de evde özel bir çalışma alanı isteyenler için maksimum büyüklükte optimize edildi.',
      image: '/yapsat-int-2.png',
      blueprint: '/plan-4-1.png'
    }
  ];

  const [plans, setPlans] = useState(fullPlans);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const docRef = doc(db, 'pages', 'floorplans');
        const snap = await getDoc(docRef);
        
        if (snap.exists() && snap.data().items && snap.data().items.length > 0) {
          const dbItems = snap.data().items.map((dbPlan: any) => {
             const defaultPlan = fullPlans.find(p => p.id === dbPlan.id) || fullPlans[0];
             
             // Eğer DB'den gelen açıklama 3 nokta ile biten o sahte/kısa metinse (veya dummy ise), orijinal uzun metni yükle
             const isCorruptDesc = dbPlan.desc && (dbPlan.desc.includes('...') || dbPlan.desc.length < 50);
             const finalDesc = isCorruptDesc ? defaultPlan.desc : (dbPlan.desc || defaultPlan.desc);
             
             return {
                ...dbPlan,
                desc: finalDesc,
                title: dbPlan.title || defaultPlan.title,
                net: dbPlan.net || defaultPlan.net,
                gross: dbPlan.gross || defaultPlan.gross,
                image: dbPlan.image || defaultPlan.image,
                blueprint: dbPlan.blueprint || defaultPlan.blueprint
             };
          });
          
          // Eksik planları ekle (önceden 3 tane kaydettiysek 6 tane olsun diye)
          const mergedItems = [...dbItems];
          fullPlans.forEach(fp => {
            if (!mergedItems.find(m => m.id === fp.id)) {
              mergedItems.push(fp);
            }
          });
          
          mergedItems.sort((a, b) => fullPlans.findIndex(fp => fp.id === a.id) - fullPlans.findIndex(fp => fp.id === b.id));
          
          setPlans(mergedItems);
          // Orijinal içerikleri Firestore'a geri bas (temizle)
          await setDoc(docRef, { items: mergedItems, updatedAt: new Date() }, { merge: true });
        } else {
          await setDoc(docRef, { items: fullPlans, updatedAt: new Date() }, { merge: true });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleChange = (id: string, field: string, value: string) => {
    setPlans(plans.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const docRef = doc(db, 'pages', 'floorplans');
      await setDoc(docRef, { items: plans, updatedAt: new Date() }, { merge: true });
      setMessage('Kat Planları detayları başarıyla güncellendi!');
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      console.error(err);
      setMessage('Kayıt başarısız oldu.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-zinc-500">Yükleniyor...</div>;

  return (
    <div className="space-y-6 pb-24">
      <div className="mb-6">
        <h2 className="text-2xl font-black text-white uppercase tracking-widest" style={{ fontFamily: 'Raleway, sans-serif' }}>Kat Planları Yönetimi</h2>
        <p className="text-zinc-400 text-sm mt-1">Sitedeki daire tiplerinin metrekare, açıklama ve görsel kısımlarını(kroki dahil) buradan düzenleyin.</p>
      </div>

      {message && (
        <div className={`p-4 rounded-xl font-bold text-sm ${message.includes('başarısız') ? 'bg-red-500/10 text-red-500' : 'bg-green-500/10 text-green-500'}`}>
          {message}
        </div>
      )}

      <div className="space-y-6">
        {plans.map((plan) => (
          <motion.div key={plan.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 md:p-8">
            <div className="flex items-center justify-between mb-6 border-b border-zinc-800 pb-4">
              <div className="flex items-center gap-3">
                <i className="ri-layout-masonry-line text-2xl text-red-500"></i>
                <h3 className="text-lg font-bold text-white uppercase tracking-wider">{plan.title} Detayları</h3>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-400 text-xs font-bold mb-1 uppercase tracking-wider">Net Metrekare (m²)</label>
                  <input type="number" value={plan.net} onChange={e => handleChange(plan.id, 'net', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-2 px-4 focus:border-red-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-xs font-bold mb-1 uppercase tracking-wider">Brüt Metrekare (m²)</label>
                  <input type="number" value={plan.gross} onChange={e => handleChange(plan.id, 'gross', e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-2 px-4 focus:border-red-500 focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-zinc-400 text-xs font-bold mb-1 uppercase tracking-wider">Pazarlama Açıklaması</label>
                <textarea value={plan.desc} onChange={e => handleChange(plan.id, 'desc', e.target.value)} rows={2} className="w-full bg-zinc-950 border border-zinc-800 text-zinc-300 rounded-xl py-2 px-4 focus:border-red-500 focus:outline-none"></textarea>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                <ImageUploader 
                  currentImageUrl={plan.image || ''} 
                  onImageUploaded={(url) => handleChange(plan.id, 'image', url)} 
                  onImageDeleted={() => handleChange(plan.id, 'image', '')} 
                  folderPath={`pages/floorplans/${plan.id}_image`}
                  label="3D Plan Görseli / Render"
                />
                <ImageUploader 
                  currentImageUrl={plan.blueprint || ''} 
                  onImageUploaded={(url) => handleChange(plan.id, 'blueprint', url)} 
                  onImageDeleted={() => handleChange(plan.id, 'blueprint', '')} 
                  folderPath={`pages/floorplans/${plan.id}_blueprint`}
                  label="Teknik Çizim / Kroki (Blueprint)"
                />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* DEV KAYDET BUTONU */}
      <div className="fixed bottom-0 left-0 right-0 md:left-64 bg-zinc-950/80 backdrop-blur-lg border-t border-zinc-800 p-4 flex justify-end z-50">
         <button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-white font-black text-lg py-4 px-12 rounded-xl shadow-2xl shadow-red-600/30 transition-all flex items-center gap-3 disabled:opacity-50">
           {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
           KAT PLANLARINI KAYDET
         </button>
      </div>
    </div>
  );
}
